import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Input,
  InputPassword,
  Typography,
  Stack,
  Divider,
  InlineMessage,
} from '@xhr-labs/core-ui';
import { useAuth } from './use-auth';
import { authService } from './auth-service';
import { sanitizeRedirectPath } from '@/lib/auth/redirect-sanitizer';
import { logError } from '@/lib/logging';
import { env } from '@/lib/config/env';
import { OAUTH_STORAGE_KEYS } from './oauth-storage';
import styles from './LoginPage.module.css';

interface LocationState {
  from?: { pathname: string };
}

function hasProperty<K extends string>(obj: object, key: K): obj is object & Record<K, unknown> {
  return key in obj;
}

function isLocationState(value: unknown): value is LocationState {
  if (typeof value !== 'object' || value === null) return false;
  if (!hasProperty(value, 'from')) return true;
  if (value.from === undefined) return true;
  if (typeof value.from !== 'object' || value.from === null) return false;
  if (!hasProperty(value.from, 'pathname')) return false;
  return typeof value.from.pathname === 'string';
}

function createDevToken(): string {
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const payload = btoa(
    JSON.stringify({
      sub: 'dev-user',
      email: 'dev@localhost',
      exp: Math.floor(Date.now() / 1000) + 86400,
    })
  );
  const signature = btoa('dev-signature');
  return `${header}.${payload}.${signature}`;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, devLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSsoLoading, setIsSsoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customToken, setCustomToken] = useState('');

  const locationState = isLocationState(location.state) ? location.state : null;
  const unsanitizedFrom = locationState?.from?.pathname;
  const from = sanitizeRedirectPath(unsanitizedFrom);

  const canSubmitCredentials = EMAIL_REGEX.test(email) && password.trim().length > 0;

  const handleEmailPasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitCredentials) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const { token } = await authService.login({ email, password });
      await login(token);
      void navigate(from, { replace: true });
    } catch (err) {
      logError(err, { operation: 'handleEmailPasswordLogin' });
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleQuickDevLogin = () => {
    const mockUser = {
      id: 'dev-user',
      email: 'dev@localhost',
      name: 'Dev User',
      status: 'ACTIVE',
      group: 'XHR_ADMIN',
      permissions: [],
      companyId: null,
      employeeId: null,
      avatar: null,
      isSso: false,
    };
    devLogin(createDevToken(), mockUser);
    void navigate(from, { replace: true });
  };

  const handleCustomTokenLogin = async (token: string) => {
    try {
      await login(token);
      void navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to login');
    }
  };

  const handleGoogleLogin = async () => {
    setIsSsoLoading(true);
    setError(null);

    try {
      const state = crypto.randomUUID();
      sessionStorage.setItem(OAUTH_STORAGE_KEYS.REDIRECT_PATH, from);
      sessionStorage.setItem(OAUTH_STORAGE_KEYS.STATE, state);

      const redirectUrl = await authService.getSsoRedirectUrl('google');
      const urlWithState = `${redirectUrl}&state=${encodeURIComponent(state)}`;

      window.location.href = urlWithState;
    } catch (err) {
      logError(err, { operation: 'handleGoogleLogin' });
      setError(err instanceof Error ? err.message : 'Failed to initiate Google login');
      setIsSsoLoading(false);
    }
  };

  return (
    <div className={styles['container']}>
      <div className={styles['sideContainer']}>
        <div className={styles['leftSide']}>
          <div className={styles['logoContainer']}>
            <div className={styles['logoIcon']}>PA</div>
          </div>
          <div className={styles['brandPanel']}>
            <div className={styles['brandEyebrow']}>Platform operations</div>
            <div className={styles['brandTitle']}>Operate the platform with clear priorities.</div>
            <div className={styles['brandText']}>
              Review approvals, manage access, and respond to operational issues from one focused
              admin workspace.
            </div>
            <div className={styles['brandHighlights']}>
              <div className={styles['brandCard']}>
                <div className={styles['brandCardTitle']}>Approval queues</div>
                <div className={styles['brandCardText']}>
                  Keep onboarding and app submissions moving without losing visibility.
                </div>
              </div>
              <div className={styles['brandCard']}>
                <div className={styles['brandCardTitle']}>Access governance</div>
                <div className={styles['brandCardText']}>
                  Manage roles, user activation, and admin accountability from a single place.
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles['rightSide']}>
          <div className={styles['content']}>
            <Stack gap="xl">
              <div className={styles['header']}>
                <div className={styles['eyebrow']}>Platform Operations</div>
                <Typography variant="h1-bold">Admin Portal</Typography>
                <Typography variant="body-small" color="color-content-neutral-medium">
                  Sign in to review approvals, manage access, and keep the platform healthy.
                </Typography>
              </div>

              {error && <InlineMessage variant="negative" description={error} />}

              <form
                className={styles['form']}
                onSubmit={(e) => {
                  void handleEmailPasswordLogin(e);
                }}
              >
                <Input
                  placeholder="Email address"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  disabled={isSubmitting}
                  data-testid="email-input"
                />
                <InputPassword
                  placeholder="Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  disabled={isSubmitting}
                  data-testid="password-input"
                />
                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  variant="primary"
                  disabled={!canSubmitCredentials}
                  loading={isSubmitting}
                >
                  Sign in
                </Button>
              </form>

              <div className={styles['dividerWithLabel']}>
                <Divider />
                <Typography variant="caption-medium" color="color-content-neutral-medium">
                  or
                </Typography>
                <Divider />
              </div>

              <Stack gap="md">
                <Button
                  fullWidth
                  size="md"
                  variant="tertiary"
                  loading={isSsoLoading}
                  onClick={() => {
                    void handleGoogleLogin();
                  }}
                  leftItem={
                    <img src="/images/google-logo.svg" alt="" className={styles['socialIcon']} />
                  }
                >
                  {isSsoLoading ? 'Redirecting...' : 'Continue with Google'}
                </Button>
              </Stack>

              {env.isDevelopment && (
                <>
                  <div className={styles['dividerWithLabel']}>
                    <Divider />
                    <Typography variant="caption-medium" color="color-content-neutral-medium">
                      Development Only
                    </Typography>
                    <Divider />
                  </div>
                  <div className={styles['devTools']}>
                    <div className={styles['devToolsTitle']}>Development only</div>
                    <Stack gap="sm">
                      <Button fullWidth size="md" variant="primary" onClick={handleQuickDevLogin}>
                        Quick Dev Login (Mock User)
                      </Button>
                      <Input
                        placeholder="Paste JWT token from another environment"
                        value={customToken}
                        onChange={(e) => {
                          setCustomToken(e.target.value);
                        }}
                      />
                      <Button
                        fullWidth
                        size="sm"
                        variant="secondary"
                        disabled={!customToken.trim()}
                        onClick={() => {
                          void handleCustomTokenLogin(customToken.trim());
                        }}
                      >
                        Login with Custom Token
                      </Button>
                    </Stack>
                  </div>
                </>
              )}
            </Stack>
          </div>

          <div className={styles['footer']}>
            <Typography variant="caption-normal" color="color-content-neutral-medium">
              © 2024 Admin Portal. All rights reserved.
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
}
